import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { arrayMove } from '@dnd-kit/sortable'
import { PlusIcon } from '@phosphor-icons/react'

import { Button, Divider, ToastContext } from '@reveni-io/reveni-ui'

import LoadMore from '@/common/components/LoadMore/LoadMore'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useShowMore from '@/common/hooks/useShowMore/useShowMore'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import Filters from '@/configuration/components/Filters/Filters'

import DeleteShippingMethodModal from './DeleteShippingMethodModal/DeleteShippingMethodModal'
import useFilter from './hooks/useFilter/useFilter'
import useShippingMethods from './hooks/useShippingMethods/useShippingMethods'
import ShippingMethodsConfigurationSkeleton from './ShippingMethodsConfigurationSkeleton/ShippingMethodsConfigurationSkeleton'
import SortableShippingMethodList from './SortableShippingMethodList/SortableShippingMethodList'
import * as Styles from './styles'

const ShippingMethodsTab = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()

  const {
    shippingMethods,
    setShippingMethods,
    gettingShippingMethods,
    regions,
    carriers,
    fetchingCarriers,
    fetchingRegions,
    revertChanges,
    handleChangeEnabled,
    onSave,
    shippingMethodSelected,
    setShippingMethodSelected,
    showDeleteShippingMethodModal,
    setShowDeleteShippingMethodModal,
    editingShippingMethods,
    onDeleteShippingMethod,
    onEditShippingMethod,
    onDuplicateShippingMethod,
    onCreate,
    hasChanges,
  } = useShippingMethods({
    storeId: info?.selectedStore?.id,
    setToastInfo,
  })

  const { filteredShippingMethods, filters, setFilters } = useFilter(shippingMethods)

  const { elementsToShow, hasMoreElements, showMore, totalElements, numberOfElementsToShow } = useShowMore({
    elements: filteredShippingMethods,
    initialElementsToShow: 10,
  })

  const [dragging, setDragging] = useState(false)

  const handleOnOpenDeleteShippingMethodModal = id => {
    const shippingMethod = shippingMethods.find(shippingMethod => shippingMethod.id === id)

    setShippingMethodSelected(shippingMethod)
    setShowDeleteShippingMethodModal(true)
  }

  const onSortEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = shippingMethods.findIndex(r => r.id === active.id)
    const newIndex = shippingMethods.findIndex(r => r.id === over.id)

    setShippingMethods(shippingMethods => [...arrayMove(shippingMethods, oldIndex, newIndex)])
    setDragging(false)
    document.body.style.cursor = 'inherit'
  }

  const onSortStart = () => {
    setDragging(true)
    document.body.style.cursor = 'grabbing'
  }

  const handleShouldCancelStart = e => {
    return e?.target?.id !== 'draggable-icon'
  }

  const handleCloseDeleteShippingMethodModal = () => setShowDeleteShippingMethodModal(false)

  if (fetchingRegions || gettingShippingMethods || fetchingCarriers) {
    return <ShippingMethodsConfigurationSkeleton />
  }

  return (
    <Styles.Container>
      {hasChanges && <UnsavedChangesAlert hasPermission={hasPermission(userPermissions.editLogistics)} />}
      <Filters
        source="shippingMethods"
        regions={regions}
        filters={filters}
        setFilters={setFilters}
        queryFilterPlaceholder={t('Nombre interno')}
        carriers={carriers}
      />
      <SortableShippingMethodList
        shippingMethods={elementsToShow || []}
        regions={regions}
        onChangeEnabled={handleChangeEnabled}
        onDelete={handleOnOpenDeleteShippingMethodModal}
        onEdit={onEditShippingMethod}
        onDuplicate={onDuplicateShippingMethod}
        isEditable={hasPermission(userPermissions.editLogistics)}
        dragging={dragging}
        onSortEnd={onSortEnd}
        onSortStart={onSortStart}
        shouldCancelStart={handleShouldCancelStart}
      />
      <Divider />
      <LoadMore
        numberOfElementsToShow={numberOfElementsToShow}
        totalElements={totalElements}
        hasMoreElements={hasMoreElements}
        showMore={showMore}
      />
      <Styles.ActionsContainer>
        {hasPermission(userPermissions.editLogistics) && (
          <Button variant="text" onClick={onCreate}>
            <PlusIcon size={24} />
            {t('Añadir nuevo método')}
          </Button>
        )}
      </Styles.ActionsContainer>
      <SaveChangesButtons
        hasChanges={hasPermission(userPermissions.editLogistics) && hasChanges}
        onSave={onSave}
        onRevertChanges={revertChanges}
        loading={editingShippingMethods}
        disabled={editingShippingMethods}
      />
      <DeleteShippingMethodModal
        shippingMethod={shippingMethodSelected}
        show={showDeleteShippingMethodModal}
        onClose={handleCloseDeleteShippingMethodModal}
        onDelete={onDeleteShippingMethod}
      />
    </Styles.Container>
  )
}

export default ShippingMethodsTab
